/**
 * Compact selectable tile for arcade palettes and node lists.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";

export interface ArcadeSelectableTilePalette {
  readonly bright: string;
  readonly muted: string;
  readonly text: string;
  readonly void: string;
}

export interface ArcadeSelectableTileProps {
  readonly accent: string;
  readonly ariaLabel: string;
  readonly badge?: React.ReactNode;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly draggable?: boolean;
  readonly hotkey?: React.ReactNode;
  readonly leading?: React.ReactNode;
  readonly onClick: () => void;
  readonly onDragStart?: React.DragEventHandler<HTMLButtonElement>;
  readonly palette?: ArcadeSelectableTilePalette;
  readonly selected?: boolean;
  readonly style?: React.CSSProperties;
  readonly subtitle?: React.ReactNode;
  readonly textColor?: string;
  readonly title: React.ReactNode;
  readonly titleColor?: string;
  readonly tooltip?: string;
  readonly trailing?: React.ReactNode;
  readonly mutedColor?: string;
  readonly voidColor?: string;
}

export function ArcadeSelectableTile({
  accent,
  ariaLabel,
  badge,
  className,
  disabled = false,
  draggable = false,
  hotkey,
  leading,
  mutedColor,
  onClick,
  onDragStart,
  palette,
  selected = false,
  style,
  subtitle,
  textColor,
  title,
  titleColor,
  tooltip,
  trailing,
  voidColor,
}: ArcadeSelectableTileProps): React.JSX.Element {
  const resolvedMutedColor = mutedColor ?? palette?.muted ?? "#94a3b8";
  const resolvedTextColor = textColor ?? palette?.text ?? "#e5e7eb";
  const resolvedTitleColor = titleColor ?? palette?.bright ?? resolvedTextColor;
  const resolvedVoidColor = voidColor ?? palette?.void ?? "#020617";

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-pressed={selected}
      className={className}
      disabled={disabled}
      draggable={draggable}
      onClick={onClick}
      onDragStart={onDragStart}
      title={tooltip}
      style={{
        background: selected ? `${accent}1A` : `${resolvedVoidColor}99`,
        border: `1px solid ${selected ? accent : `${resolvedMutedColor}66`}`,
        borderRadius: 10,
        color: resolvedTextColor,
        cursor: disabled ? "default" : "pointer",
        fontFamily: "inherit",
        fontSize: 12,
        opacity: disabled ? 0.55 : 1,
        padding: "9px 11px",
        textAlign: "left",
        ...style,
      }}
    >
      <span className="flex flex-wrap items-center gap-1.5">
        {leading !== undefined ? (
          <span aria-hidden style={{ color: accent }}>
            {leading}
          </span>
        ) : null}
        <strong style={{ color: resolvedTitleColor }}>{title}</strong>
        {badge}
        {trailing !== undefined ? (
          <span
            className="ml-auto text-[9.5px]"
            style={{ color: resolvedMutedColor }}
          >
            {trailing}
          </span>
        ) : null}
        {hotkey !== undefined ? (
          <span
            aria-hidden
            className="ml-auto text-[9.5px]"
            style={{ color: resolvedMutedColor }}
          >
            {hotkey}
          </span>
        ) : null}
      </span>
      {subtitle !== undefined ? (
        <span
          className="mt-1 block text-[10.5px] leading-[1.5]"
          style={{ color: resolvedMutedColor }}
        >
          {subtitle}
        </span>
      ) : null}
    </button>
  );
}
